import { type Conversation } from '@shared/all/domains/messages';

import {
  type ConversationAction,
  conversationActions,
  type ConversationsState,
  initialConversationsState,
  type MessagesState
} from '../context';
import { sameValue } from './sameValue';

// Max number of open conversations (includes minimized)
const MAX_OPEN_CONVERSATIONS = 5;
// Max number of minimized conversations shown as pills (5 = 1x5 row on mobile)
const MAX_MINIMIZED_CONVERSATIONS = 5;

export const conversationReducer = (
  state: ConversationsState,
  action: ConversationAction
) => {
  let updatedState = state;
  switch (action.type) {
    case conversationActions.TOGGLE_CONVERSATION_LIST:
      updatedState = {
        ...state,
        conversationListOpen: !state.conversationListOpen
      };
      break;
    case conversationActions.SET_CONVERSATIONS: {
      // The list query can hand over a first page older than the store: a
      // next page is appended to the first page as it was fetched, and a
      // next-page fetch cancels a refetch in flight. A conversation the
      // sockets have since moved on (a newer last message), or that was read
      // here since, keeps the store's copy, and one with a newer message goes
      // back to the top, as it went when the message arrived.
      const newerHere: Conversation[] = [];
      const conversations = action.payload.flatMap((conv: Conversation) => {
        const existingConv = state.conversations.find(
          (c) => c.id === conv.id
        );
        const existingId = existingConv?.lastMessage?.id ?? 0;
        const incomingId = conv.lastMessage?.id ?? 0;
        if (existingConv && existingId > incomingId) {
          newerHere.push(existingConv);
          return [];
        }
        const readHere =
          existingConv?.read === true && existingId === incomingId;
        return [
          {
            ...conv,
            ...(readHere ? { read: true, unreadCount: 0 } : {}),
            usersTyping: existingConv?.usersTyping || []
          }
        ];
      });
      updatedState = {
        ...state,
        conversations: [...newerHere, ...conversations]
      };
      break;
    }

    case conversationActions.UPDATE_CONVERSATION_STATE:
      let openConversations = state.openConversations.filter(
        (conv) => conv.id !== action.payload.conversationId
      );
      if (action.payload.state !== 'CLOSED') {
        // When opening a conversation, minimize others and enforce limits
        if (action.payload.state === 'OPEN') {
          // Remove oldest if at max open conversations (only when adding a new one)
          if (openConversations.length >= MAX_OPEN_CONVERSATIONS) {
            openConversations.pop();
          }
          openConversations = [
            {
              id: action.payload.conversationId,
              conversationState: action.payload.state
            },
            ...openConversations.map((conversation) => ({
              id: conversation.id,
              conversationState: 'MINIMIZED' as const
            }))
          ];
        } else if (action.payload.state === 'MINIMIZED') {
          // When minimizing, check if we're at max minimized limit
          const currentMinimized = openConversations.filter(
            (conv) => conv.conversationState === 'MINIMIZED'
          );
          // If at max minimized, remove the oldest minimized one
          if (currentMinimized.length >= MAX_MINIMIZED_CONVERSATIONS) {
            const oldestMinimizedId =
              currentMinimized[currentMinimized.length - 1]?.id;
            openConversations = openConversations.filter(
              (conv) => conv.id !== oldestMinimizedId
            );
          }
          openConversations = [
            {
              id: action.payload.conversationId,
              conversationState: action.payload.state
            },
            ...openConversations
          ];
        }
      }
      return {
        ...state,
        openConversations
      };
    case conversationActions.SET_TYPING: {
      // Typing events repeat every keystroke; one already shown is no change.
      const typingConv = state.conversations.find(
        (conv) => conv.id === action.payload.conversationId
      );
      if (
        !typingConv ||
        typingConv.usersTyping.includes(action.payload.userId)
      ) {
        return state;
      }
      updatedState = {
        ...state,
        conversations: state.conversations.map((conv) => {
          if (conv.id === action.payload.conversationId) {
            return {
              ...conv,
              usersTyping: [...conv.usersTyping, action.payload.userId]
            };
          }
          return conv;
        })
      };
      break;
    }
    case conversationActions.REMOVE_TYPING: {
      const typingConv = state.conversations.find(
        (conv) => conv.id === action.payload.conversationId
      );
      if (!typingConv?.usersTyping.includes(action.payload.userId)) {
        return state;
      }
      updatedState = {
        ...state,
        conversations: state.conversations.map((conv) => {
          if (conv.id === action.payload.conversationId) {
            return {
              ...conv,
              usersTyping: conv.usersTyping.filter(
                (userId) => userId !== action.payload.userId
              )
            };
          }
          return conv;
        })
      };
      break;
    }
    case conversationActions.RESET_CONVERSATIONS:
      updatedState = initialConversationsState;
      break;
    case conversationActions.SET_OPEN_CONVERSATIONS:
      updatedState = {
        ...state,
        openConversations: action.payload
      };
      break;
    case conversationActions.UPDATE_ACTIVITY_STATUS: {
      const participant = state.conversations
        .find((conv) => conv.id === action.payload.conversationId)
        ?.participants.find((p) => p.id === action.payload.userId);
      if (
        !participant ||
        (participant.isActive === action.payload.isActive &&
          sameValue(participant.lastActive, action.payload.lastActive))
      ) {
        return state;
      }
      updatedState = {
        ...state,
        conversations: state.conversations.map((conv) => {
          if (conv.id === action.payload.conversationId) {
            return {
              ...conv,
              participants: conv.participants.map((p) =>
                p.id === action.payload.userId
                  ? {
                      ...p,
                      lastActive: action.payload.lastActive,
                      isActive: action.payload.isActive
                    }
                  : p
              )
            };
          }
          return conv;
        })
      };
      break;
    }
    case conversationActions.ADD_CONVERSATIONS: {
      const newConvs = action.payload.filter(
        (conv) => !state.conversations.some((c) => c.id === conv.id)
      );
      if (newConvs.length === 0) return state;
      updatedState = {
        ...state,
        conversations: [...newConvs, ...state.conversations]
      };
      break;
    }
    case conversationActions.REMOVE_CONVERSATION:
      updatedState = {
        ...state,
        conversations: state.conversations.filter(
          (conv) => conv.id !== action.payload
        ),
        openConversations: state.openConversations.filter(
          (conv) => conv.id !== action.payload
        )
      };
      break;
    case conversationActions.UPDATE_CONVERSATION_NAME:
      updatedState = {
        ...state,
        conversations: state.conversations.map((conv) =>
          conv.id === action.payload.conversationId
            ? { ...conv, name: action.payload.name }
            : conv
        )
      };
      break;
    case conversationActions.UPDATE_READ_RECEIPT:
      if (
        !state.conversations.some(
          (conv) => conv.id === action.payload && !conv.read
        )
      ) {
        return state;
      }
      updatedState = {
        ...state,
        conversations: state.conversations.map((conv) =>
          // Read means nothing unread: zero the count too, or the next message
          // shows the count from before the conversation was read.
          conv.id === action.payload
            ? { ...conv, read: true, unreadCount: 0 }
            : conv
        )
      };
      break;
    case conversationActions.UPDATE_LAST_MESSAGE: {
      const conv = state.conversations.find(
        (c) => c.id === action.payload.conversationId
      );
      if (!conv) {
        return state;
      }
      // Keep read: true if conversation is currently open (user is viewing it)
      const isCurrentlyOpen = state.openConversations.some(
        (oc) =>
          oc.id === action.payload.conversationId &&
          oc.conversationState === 'OPEN'
      );
      // The list's unread badge counts from the conversation's last fetch, so
      // a live message adds one to it (starting from zero if it had been read).
      // The user's own message, sent from another device, adds nothing.
      const unreadBefore = conv.read ? 0 : (conv.unreadCount ?? 0);
      const unreadAdded = action.payload.fromSelf ? 0 : 1;
      const updated = {
        ...conv,
        lastMessage: action.payload.lastMessage,
        read: isCurrentlyOpen,
        unreadCount: isCurrentlyOpen ? 0 : unreadBefore + unreadAdded
      };
      updatedState = {
        ...state,
        conversations: [
          updated,
          ...state.conversations.filter(
            (c) => c.id !== action.payload.conversationId
          )
        ]
      };
      break;
    }
    case conversationActions.REMOVE_PARTICIPANT:
      updatedState = {
        ...state,
        conversations: state.conversations.map((conv) =>
          conv.id === action.payload.conversationId
            ? {
                ...conv,
                participants: conv.participants.filter(
                  (p) => p.id !== action.payload.participantId
                )
              }
            : conv
        )
      };
      break;
    case conversationActions.REJECT_DISPUTE:
      updatedState = {
        ...state,
        conversations: state.conversations.map((conv) =>
          conv.id === action.payload && conv.ban
            ? { ...conv, ban: { ...conv.ban, disputeRejected: true } }
            : conv
        )
      };
      break;

    // Message actions
    case conversationActions.SET_MESSAGES: {
      const { conversationId, messages, hasNextPage, cursor, hasNewerPage } =
        action.payload;
      // Dedupe messages by ID (in case of race conditions with socket)
      const seen = new Set<number>();
      const dedupedMessages = messages.filter((m) => {
        if (seen.has(m.id)) return false;
        seen.add(m.id);
        return true;
      });
      const newMessagesState: MessagesState = {
        messages: dedupedMessages,
        cursor,
        hasNextPage,
        hasNewerPage: hasNewerPage ?? false,
        heldCount: 0,
        isLoading: false,
        error: null
      };
      updatedState = {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: newMessagesState
        }
      };
      break;
    }

    case conversationActions.ADD_MESSAGES: {
      const { conversationId, messages, hasNextPage, cursor } = action.payload;
      const existingState = state.messages[conversationId];
      if (!existingState) return state;
      // Dedupe: only add messages not already in existing state
      const existingIds = new Set(existingState.messages.map((m) => m.id));
      const newMessages = messages.filter((m) => !existingIds.has(m.id));
      updatedState = {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: {
            ...existingState,
            messages: [...existingState.messages, ...newMessages],
            cursor,
            hasNextPage,
            isLoading: false
          }
        }
      };
      break;
    }

    case conversationActions.ADD_NEWER_MESSAGES: {
      const { conversationId, messages, hasNewerPage, afterId } =
        action.payload;
      const existingState = state.messages[conversationId];
      if (!existingState?.hasNewerPage) return state;
      if (existingState.messages.find((m) => m.id > 0)?.id !== afterId) {
        return state;
      }
      const existingIds = new Set(existingState.messages.map((m) => m.id));
      const newMessages = messages.filter((m) => !existingIds.has(m.id));
      const merged = [...newMessages, ...existingState.messages];
      // A message held while this page was in flight may have missed it. The
      // conversation's last message says so, and one more page picks it up.
      const lastMessageId = state.conversations.find(
        (c) => c.id === conversationId
      )?.lastMessage?.id;
      const newestLoadedId = merged.find((m) => m.id > 0)?.id ?? 0;
      const stillBehind =
        hasNewerPage ||
        (existingState.heldCount > 0 &&
          lastMessageId !== undefined &&
          lastMessageId > newestLoadedId);
      updatedState = {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: {
            ...existingState,
            messages: merged,
            hasNewerPage: stillBehind,
            // Reaching the live end loaded everything that was held.
            heldCount: stillBehind ? existingState.heldCount : 0
          }
        }
      };
      break;
    }

    case conversationActions.PREPEND_MESSAGE: {
      const { conversationId, message } = action.payload;
      const existingState = state.messages[conversationId];
      // If no existing state, create one with just this message
      if (!existingState) {
        updatedState = {
          ...state,
          messages: {
            ...state.messages,
            [conversationId]: {
              messages: [message],
              cursor: undefined,
              hasNextPage: true,
              hasNewerPage: false,
              heldCount: 0,
              isLoading: false,
              error: null
            }
          }
        };
        break;
      }
      // Check if message already exists (avoid duplicates)
      if (existingState.messages.some((m) => m.id === message.id)) {
        return state;
      }
      // The window is detached from the live end: a live message belongs
      // after messages that are not loaded, so inserting it here would drop
      // it into the middle of history. Count it instead; it is loaded when
      // the window reaches the live end or the user goes back to now.
      if (existingState.hasNewerPage && message.id > 0) {
        updatedState = {
          ...state,
          messages: {
            ...state.messages,
            [conversationId]: {
              ...existingState,
              heldCount: existingState.heldCount + 1
            }
          }
        };
        break;
      }
      // When a real message arrives (positive id), replace the matching
      // pending temp from the same sender so both never coexist in the
      // same render frame (eliminates the duplicate flash + scroll bounce).
      //
      // The *oldest* pending temp: the list is newest-first, so findIndex
      // paired an echo with the newest bubble — send 1..7 and the echo for 1
      // landed in 7's slot. Sends are serialised, so oldest is the partner.
      if (message.id > 0 && message.sender) {
        let pendingIdx = -1;
        for (let i = existingState.messages.length - 1; i >= 0; i--) {
          const m = existingState.messages[i]!;
          if (
            m.id < 0 &&
            m.pending === true &&
            m.sender.id === message.sender.id
          ) {
            pendingIdx = i;
            break;
          }
        }
        if (pendingIdx !== -1) {
          const replaced = [...existingState.messages];
          replaced[pendingIdx] = message;
          updatedState = {
            ...state,
            messages: {
              ...state.messages,
              [conversationId]: {
                ...existingState,
                messages: replaced
              }
            }
          };
          break;
        }
      }
      updatedState = {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: {
            ...existingState,
            messages: [message, ...existingState.messages]
          }
        }
      };
      break;
    }

    case conversationActions.MARK_MESSAGE_FAILED: {
      const { conversationId, messageId } = action.payload;
      const existingState = state.messages[conversationId];
      if (!existingState) return state;
      updatedState = {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: {
            ...existingState,
            messages: existingState.messages.map((m) =>
              m.id === messageId ? { ...m, pending: false, failed: true } : m
            )
          }
        }
      };
      break;
    }

    case conversationActions.UPDATE_MESSAGE: {
      const { conversationId, messageId, content, edited } = action.payload;
      const existingState = state.messages[conversationId];
      if (!existingState) return state;
      updatedState = {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: {
            ...existingState,
            messages: existingState.messages.map((m) =>
              m.id === messageId ? { ...m, content, edited } : m
            )
          }
        }
      };
      break;
    }

    case conversationActions.PATCH_MESSAGE: {
      const { conversationId, messageId, patch } = action.payload;
      const existingState = state.messages[conversationId];
      // The conversation may not be open — a reaction arriving for a thread
      // nobody has loaded is simply dropped; the next fetch carries it.
      if (!existingState) return state;
      updatedState = {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: {
            ...existingState,
            messages: existingState.messages.map((m) =>
              m.id === messageId ? { ...m, ...patch } : m
            )
          }
        }
      };
      break;
    }

    case conversationActions.DELETE_MESSAGE: {
      const { conversationId, messageId } = action.payload;
      const existingState = state.messages[conversationId];
      // Blank the content too — the bubble renders a tombstone off `deleted`,
      // and leaving the text around would keep it visible in the list preview.
      const tombstone = { content: '', deleted: true, attachments: [] };

      updatedState = {
        ...state,
        ...(existingState && {
          messages: {
            ...state.messages,
            [conversationId]: {
              ...existingState,
              messages: existingState.messages.map((m) =>
                m.id === messageId ? { ...m, ...tombstone } : m
              )
            }
          }
        }),
        conversations: state.conversations.map((conv) =>
          conv.id === conversationId && conv.lastMessage?.id === messageId
            ? { ...conv, lastMessage: { ...conv.lastMessage, ...tombstone } }
            : conv
        )
      };
      break;
    }

    case conversationActions.RESTORE_MESSAGE: {
      const { conversationId, messageId, content, attachments } =
        action.payload;
      const existingState = state.messages[conversationId];

      updatedState = {
        ...state,
        ...(existingState && {
          messages: {
            ...state.messages,
            [conversationId]: {
              ...existingState,
              messages: existingState.messages.map((m) =>
                m.id === messageId
                  ? {
                      ...m,
                      content,
                      attachments,
                      deleted: false,
                      restorable: false
                    }
                  : m
              )
            }
          }
        }),
        conversations: state.conversations.map((conv) =>
          conv.id === conversationId && conv.lastMessage?.id === messageId
            ? {
                ...conv,
                lastMessage: { ...conv.lastMessage, content, deleted: false }
              }
            : conv
        )
      };
      break;
    }

    case conversationActions.SET_MESSAGES_LOADING: {
      const { conversationId, isLoading } = action.payload;
      const existingState = state.messages[conversationId] || {
        messages: [],
        cursor: undefined,
        hasNextPage: true,
        hasNewerPage: false,
        heldCount: 0,
        isLoading: false,
        error: null
      };
      updatedState = {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: {
            ...existingState,
            isLoading
          }
        }
      };
      break;
    }

    case conversationActions.SET_MESSAGES_ERROR: {
      const { conversationId, error } = action.payload;
      const existingState = state.messages[conversationId];
      if (!existingState) return state;
      updatedState = {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: {
            ...existingState,
            error,
            isLoading: false
          }
        }
      };
      break;
    }

    case conversationActions.CLEAR_MESSAGES: {
      const conversationId = action.payload;
      const { [conversationId]: _, ...remainingMessages } = state.messages;
      updatedState = {
        ...state,
        messages: remainingMessages
      };
      break;
    }

    default:
      return state;
  }
  return updatedState;
};
